import React, { useRef, useEffect } from 'react';

interface DropdownMenuItem {
  id: string;
  label: string;
  icon?: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
}

interface DropdownMenuProps {
  items: DropdownMenuItem[];
  isOpen: boolean;
  onClose: () => void;
  trigger: React.ReactNode;
  className?: string;
}

export const DropdownMenu: React.FC<DropdownMenuProps> = ({
  items,
  isOpen,
  onClose,
  trigger,
  className = ''
}) => {
  const menuRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target as Node) &&
        triggerRef.current &&
        !triggerRef.current.contains(event.target as Node)
      ) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  // Focus first item on open + keyboard navigation
  useEffect(() => {
    if (!isOpen) return;

    // Focus first non-disabled item
    const firstItem = menuRef.current?.querySelector('[role="menuitem"]:not([disabled])') as HTMLElement;
    firstItem?.focus();

    const handleKeyDown = (e: KeyboardEvent) => {
      const menuItems = menuRef.current?.querySelectorAll('[role="menuitem"]:not([disabled])');
      if (!menuItems) return;
      const itemsArray = Array.from(menuItems) as HTMLElement[];
      const currentIndex = itemsArray.indexOf(document.activeElement as HTMLElement);

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          itemsArray[(currentIndex + 1) % itemsArray.length]?.focus();
          break;
        case 'ArrowUp':
          e.preventDefault();
          itemsArray[currentIndex <= 0 ? itemsArray.length - 1 : currentIndex - 1]?.focus();
          break;
        case 'Home':
          e.preventDefault();
          itemsArray[0]?.focus();
          break;
        case 'End':
          e.preventDefault();
          itemsArray[itemsArray.length - 1]?.focus();
          break;
        case 'Escape':
          onClose();
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  return (
    <div className={`relative ${className}`}>
      <div ref={triggerRef}>
        {trigger}
      </div>

      {isOpen && (
        <div
          ref={menuRef}
          role="menu"
          className="absolute right-0 top-full mt-1 w-48 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-50 py-1"
        >
          {items.map((item) => (
            <button
              key={item.id}
              role="menuitem"
              tabIndex={-1}
              onClick={() => {
                if (!item.disabled) {
                  item.onClick();
                  onClose();
                }
              }}
              disabled={item.disabled}
              aria-disabled={item.disabled}
              className={`w-full px-4 py-2 text-left text-sm flex items-center space-x-3 transition-colors focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500 ${
                item.disabled
                  ? 'text-gray-400 dark:text-gray-500 cursor-not-allowed'
                  : 'text-gray-700 hover:text-gray-900 hover:bg-gray-50 dark:text-gray-300 dark:hover:text-white dark:hover:bg-gray-700'
              }`}
            >
              {item.icon && (
                <span className="flex-shrink-0">
                  {item.icon}
                </span>
              )}
              <span>{item.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
